import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../../../utils/firebase.utils';
import { applyActionCode, checkActionCode } from 'firebase/auth';

const EmailActionHandler: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleEmailAction = async () => {
      const mode = searchParams.get('mode');
      const oobCode = searchParams.get('oobCode');
      const continueUrl = searchParams.get('continueUrl');

      if (!mode || !oobCode) {
        setError('Invalid email action link');
        setIsProcessing(false);
        return;
      }

      try {
        switch (mode) {
          case 'verifyEmail':
            // Verify the email verification code
            await checkActionCode(auth, oobCode);
            await applyActionCode(auth, oobCode);
            
            // Reload the user to get updated verification status
            if (auth.currentUser) {
              await auth.currentUser.reload();
            }
            
            // Redirect to success page
            navigate('/email-verification-success');
            break;
            
          case 'resetPassword':
            // Redirect to password reset page with the code
            navigate(`/reset-password?oobCode=${oobCode}`);
            break;
            
          default:
            setError('Unknown email action');
            setIsProcessing(false);
        }
      } catch (error: any) {
        console.error('Email action error:', error);
        setError(error.message || 'Failed to process email action');
        setIsProcessing(false);
      }
    };

    handleEmailAction();
  }, [searchParams, navigate]);

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-gray-100 to-blue-50">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center mx-auto mb-6">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
            
            <h1 className="text-2xl font-bold mb-4 font-inter">
              Processing Email Action
            </h1>
            
            <p className="text-gray-600 mb-6">
              Please wait while we process your email verification...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-gray-100 to-blue-50">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-400 to-pink-500 flex items-center justify-center mx-auto mb-6">
              <span className="text-white text-3xl">
                <i className="fas fa-exclamation-triangle" />
              </span>
            </div>
            
            <h1 className="text-2xl font-bold mb-4 font-inter text-red-600">
              Action Failed
            </h1>
            
            <p className="text-gray-600 mb-6">
              {error}
            </p>
            
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-bold text-base rounded-lg h-12 flex items-center justify-center transition-all"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default EmailActionHandler;